import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Expand, Maximize2, PanelLeftClose, PanelLeftOpen, Plus, Search, Shrink, Trash2, Users, Video, X } from 'lucide-react';
import baseapi, { API_BASE, useApiOutage } from '../baseapi';
import { LiveFrame } from '../components/junctions/LiveFrame';
import { readLast, saveLast } from '../utils/lastAnswer';

/**
 * Video wall: a grid of live camera views that the operator fills by dragging cameras in from the list.
 *
 * Drag a camera onto a cell (or click a camera to put it into the first free cell), drag one cell onto
 * another to swap them, and choose 2x2 / 3x3 / 4x4. The layout is remembered in this browser.
 *
 * Each cell shows the camera's saved picture (the server keeps one plain frame per camera every 10 minutes,
 * the same picture as the Cameras page) with the time it was taken; click a cell for that camera's live video.
 * 13 Oct 2026: the cells were live views that asked for a new picture every second, so every camera on an
 * open wall counted as watched: the analytics service drew every frame of it, the view-only cameras on it
 * stayed open, and the Antharalayam door cameras fell to ~1 analysed frame per second.
 */
interface Cam {
  id: string; name: string; ip: string | null; online: boolean; state: string;
  group_name: string | null; checkpoint_name: string | null; server: 'local' | 'remote'; tracks: number | null;
  group?: string | null; checkpoint?: string | null; ps?: string | null;
  /** false = view only: the server opens this camera's stream when a view asks for it */
  analytics?: boolean;
  /** the camera's saved picture (cameras/{id}/last-snapshot.jpg) and when it was taken (s) */
  snapshot_url?: string | null; snapshot_ts?: number | null;
}

/** Saved picture of a camera: the small one for small cells; ?t= changes when a new picture is saved. */
const pictureSrc = (c: Cam | undefined, small: boolean) =>
  c?.snapshot_url ? `${API_BASE}${c.snapshot_url}?${small ? 'thumb=1&' : ''}t=${Math.floor(c.snapshot_ts ?? 0)}` : null;
const pictureTime = (ts?: number | null) =>
  ts ? new Date(ts * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }) : '';

/** A holding-area tile kept live (15 Oct 2026, operators: "with many cameras the video does not come, one camera
 *  alone does"). It asks every few seconds for the camera's plain decoded frame, still.jpg - the same source as the
 *  backend's holding wall (videowall.py) - which the analytics process answers without drawing or encoding anything
 *  extra, unlike snapshot.jpg (the reason the wall cells became saved pictures on 13 Oct). Staggered, paused while
 *  the page is hidden, and the old picture stays until the new one has arrived, so nothing flickers. */
function StillFeed({ camId, width, every, className }: { camId: string; width: number; every: number; className: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const next = () => {
      if (!alive) return;
      if (document.hidden) { timer = setTimeout(next, every); return; }
      const url = `${API_BASE}cameras/${encodeURIComponent(camId)}/still.jpg?w=${width}&q=70&t=${Date.now()}`;
      const img = new Image();
      img.onload = () => { if (!alive) return; setSrc(url); timer = setTimeout(next, every); };
      img.onerror = () => { if (alive) timer = setTimeout(next, every * 2); };
      img.src = url;
    };
    timer = setTimeout(next, Math.random() * Math.min(every, 1500));
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [camId, width, every]);
  return src ? <img src={src} alt={camId} draggable={false} className={className} /> : null;
}

/** A view-only camera is closed until somebody looks at it: it can be shown although it is not "online". */
const onDemand = (c?: Cam) => !!c && c.analytics === false && !c.online && c.state !== 'unavailable';

/** Where a camera is, for the area tabs: worked out from its checkpoint / group ids. */
const AREAS = ['Temple', 'Holding area', 'Junctions', 'Railway station', 'Bus stand'] as const;
type Area = typeof AREAS[number];
function areaOf(c: Cam): Area {
  const cp = (c.checkpoint || '').toUpperCase();
  const t = `${c.ps || ''} ${cp} ${c.group || ''}`.toUpperCase();
  if (t.includes('HOLDING') || cp.startsWith('CP-HA')) return 'Holding area';
  if (c.server === 'local') return 'Temple';
  // City cameras: the station's and the bus stand's own gates by their checkpoint id; every other one
  // (Vinayaka, Geddabomma, Canal Road ...) is a junction on the way, whatever approach group it belongs to.
  if (cp.startsWith('PNBS') || cp.startsWith('BUS')) return 'Bus stand';
  if (cp.startsWith('RAIL')) return 'Railway station';
  return 'Junctions';
}

/** The queue-line cameras by address, in order along the queue (Vinayaka Junction first). A tab of their own
 *  beside the area tabs: each of them is also in its area (Temple, Junctions ...). */
const QUEUE = 'Queue lines';
const QUEUE_LINE_IPS = [
  '192.168.2.102', '192.168.2.103', '192.168.2.105', '192.168.2.133', '192.168.2.117', '192.168.2.108',
  '192.168.2.109', '192.168.2.104', '192.168.0.5', '192.168.2.18', '192.168.2.167', '192.168.2.151',
  '192.168.2.138', '192.168.2.115', '192.168.2.112', '192.168.2.113',
];
const queuePos = (c: Cam) => QUEUE_LINE_IPS.indexOf((c.ip || '').split(':')[0]);
type Tab = Area | typeof QUEUE;
const TABS: readonly Tab[] = [...AREAS, QUEUE];
const inTab = (c: Cam, t: Tab | '*' | 'All') =>
  t === '*' || t === 'All' || (t === QUEUE ? queuePos(c) >= 0 : areaOf(c) === t);
/** The queue-line tab keeps the cameras in their order along the queue. */
const tabOrder = (t: Tab | '*' | 'All') => (a: Cam, b: Cam) => (t === QUEUE ? queuePos(a) - queuePos(b) : 0);

const SIZES = [1, 2, 3, 4, 5, 6] as const;
const MAX_CELLS = 250;          // all pages together; one page shows n x n of them
const STORE = 'video_wall_layout_v1';
const MIME = 'application/x-camera-id';
const pref0 = (k: string, d: string) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };

function loadLayout(): { n: number; cells: (string | null)[] } {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) || 'null');
    if (v && SIZES.includes(v.n) && Array.isArray(v.cells)) return { n: v.n, cells: v.cells.slice(0, MAX_CELLS) };
  } catch { /* first visit, or storage not available */ }
  return { n: 4, cells: [] };
}

export function VideoWallPage() {
  const [cams, setCams] = useState<Cam[]>(() => readLast<Cam[]>('wall-cameras') || []);   // opens on the last known states
  const [error, setError] = useState(false);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const failsRef = useRef(0);          // refreshes that failed in a row
  const [q, setQ] = useState('');
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [area, setArea] = useState<Tab | 'All'>('All');
  const initial = useRef(loadLayout());
  // One holding area's wall (15 Oct 2026): /video-wall?holding=HA-1, opened by the "Video wall" button of the
  // Overview's holding cards. Only that area's cameras - its entry, the boxes in their order, its exit - sized to fit
  // one page; the operator's own wall size and layout are left as they were.
  const [params] = useSearchParams();
  const holding = params.get('holding');
  const [hold, setHold] = useState<{ name: string; ids: string[] } | null>(null);
  const [holdN, setHoldN] = useState(5);
  useEffect(() => {
    if (!holding) { setHold(null); return; }
    let alive = true;
    baseapi.get('holding').then(r => {
      const a = (r.data?.areas || []).find((x: any) => String(x.id).toUpperCase() === holding.toUpperCase());
      if (!alive || !a) return;
      const id = (c: any) => (typeof c === 'string' ? c : c?.id);
      const boxes = [...(a.boxes || [])].sort((x: any, y: any) => Number(x.number) - Number(y.number));
      const ids = [...(a.entry_cameras || []).map(id), ...boxes.flatMap((b: any) => (b.cameras || []).slice(0, 1).map(id)),
        ...(a.exit_cameras || []).map(id)].filter((x: string | undefined, i: number, all: (string | undefined)[]) => x && all.indexOf(x) === i) as string[];
      setHold({ name: `${String(a.name || a.id).split('·')[0].trim()} (${a.id})`, ids });
      setHoldN(Math.min(6, Math.max(2, Math.ceil(Math.sqrt(ids.length)))));
    }).catch(() => { /* the full wall stays */ });
    return () => { alive = false; };
  }, [holding]);
  const [nSaved, setNSaved] = useState<number>(initial.current.n);
  const n = hold ? holdN : nSaved;
  const setN = hold ? setHoldN : setNSaved;
  // One flat list of cells: page p shows cells p*n*n .. (p+1)*n*n. Only the page on screen loads video, so
  // any number of cameras can be on the wall without more load than one page.
  const [cells, setCells] = useState<(string | null)[]>(() => initial.current.cells.slice());
  const [page, setPage] = useState(0);
  // What the wall shows. Off (default): the cameras of the tab chosen on the left (All, Temple, Holding area ...),
  // page after page. On: "My layout", the wall arranged by hand, where the tabs only filter the camera list.
  const [customPref, setCustom] = useState(() => pref0('video_wall_custom', '0') === '1');
  const custom = customPref && !hold;            // one holding area's wall is never the hand-arranged one
  const [over, setOver] = useState<number | null>(null);
  const [big, setBig] = useState<string | null>(null);
  // camera list shown / hidden, picture fitted or filling its cell, wall on the whole screen
  const pref = pref0;
  const [listOpen, setListOpen] = useState(() => pref('video_wall_list', '1') === '1');
  const [fill, setFill] = useState(() => pref('video_wall_fill', '0') === '1');
  const [full, setFull] = useState(false);
  const wallRef = useRef<HTMLElement>(null);
  useEffect(() => { try { localStorage.setItem('video_wall_list', listOpen ? '1' : '0'); localStorage.setItem('video_wall_fill', fill ? '1' : '0'); localStorage.setItem('video_wall_custom', customPref ? '1' : '0'); } catch { /* not remembered */ } }, [listOpen, fill, customPref]);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === wallRef.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else wallRef.current?.requestFullscreen?.().catch(() => { /* not allowed here: the wall simply stays in the page */ });
  };

  const load = useCallback(() => baseapi.get('cameras/overview')
    .then(r => { const list = r.data?.cameras || []; setCams(list); failsRef.current = 0; setError(false); if (list.length) saveLast('wall-cameras', list); })
    // one slow or missed refresh is normal (the list is asked for every 5 s): only say so after three in a row
    .catch(() => { failsRef.current += 1; if (failsRef.current >= 3) setError(true); }), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    let end = cells.length; while (end > 0 && !cells[end - 1]) end -= 1;      // empty cells at the end are not kept
    try { localStorage.setItem(STORE, JSON.stringify({ n: nSaved, cells: cells.slice(0, end) })); } catch { /* not remembered, still works */ }
  }, [nSaved, cells]);

  const byId = useMemo(() => new Map(cams.map(c => [c.id, c])), [cams]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const match = (c: Cam) => (!onlyOnline || c.online || onDemand(c))
      && (!s || [c.id, c.name, c.ip || '', c.group_name || '', c.checkpoint_name || ''].some(x => x.toLowerCase().includes(s)));
    if (hold) return cams.filter(c => hold.ids.includes(c.id) && match(c)).sort((a, b) => hold.ids.indexOf(a.id) - hold.ids.indexOf(b.id));
    return cams.filter(c => match(c) && inTab(c, area)).sort(tabOrder(area));
  }, [cams, q, onlyOnline, area, hold]);

  const shown = n * n;
  // the wall: laid out by hand, or simply every camera of the list on the left, in the same order
  const wall = useMemo<(string | null)[]>(() => (custom ? cells : list.slice(0, MAX_CELLS).map(c => c.id)), [custom, cells, list]);
  const onWall = useMemo(() => new Set(wall.filter(Boolean) as string[]), [wall]);
  // pages that hold a camera, plus one empty page to add more on when the last one is full
  const pages = useMemo(() => {
    let last = -1; wall.forEach((x, i) => { if (x) last = i; });
    const used = Math.max(1, Math.ceil((last + 1) / shown));
    const lastFull = custom && last >= 0 && wall.slice((used - 1) * shown, used * shown).filter(Boolean).length === shown;
    return Math.min(used + (lastFull ? 1 : 0), Math.ceil(MAX_CELLS / shown));
  }, [wall, shown, custom]);
  const pg = Math.min(page, pages - 1);
  const base = pg * shown;                       // index of the first cell on screen
  const onPage = wall.slice(base, base + shown).filter(Boolean).length;
  useEffect(() => {                              // left / right arrow keys turn the page
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') setPage(Math.min(pg + 1, pages - 1));
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') setPage(Math.max(pg - 1, 0));
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [pg, pages]);

  /** Put a camera on a cell. Coming from another cell: the two cells swap. */
  const place = (camId: string, to: number, from: number | null) => setCells(cs => {
    const c = cs.slice();
    while (c.length <= Math.max(to, from ?? 0)) c.push(null);
    if (from !== null) { [c[from], c[to]] = [c[to], c[from]]; return c; }
    const was = c.indexOf(camId);
    if (was >= 0) c[was] = null;                     // a camera is on the wall once (on any page): it moves
    c[to] = camId;
    return c;
  });
  /** Click on a camera in the list: already on the wall -> go to its page; else the next free cell, from this page on. */
  const addToFirstFree = (camId: string) => {
    const at = wall.indexOf(camId);
    if (at >= 0 || !custom) { if (at >= 0) setPage(Math.floor(at / shown)); return; }
    let i = base;
    while (i < MAX_CELLS && cells[i]) i += 1;
    if (i >= MAX_CELLS) return;                 // every page is full
    setCells(cs => { const c = cs.slice(); while (c.length <= i) c.push(null); c[i] = camId; return c; });
    setPage(Math.floor(i / shown));
  };
  /** Fill the wall in one go with the cameras of one group (live ones first). */
  const areaCount = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of cams) { const a = areaOf(c); m[a] = (m[a] || 0) + 1; if (queuePos(c) >= 0) m[QUEUE] = (m[QUEUE] || 0) + 1; }
    return m;
  }, [cams]);
  /** All cameras of the group go on the wall, over as many pages as they need (live ones first). */
  /** Open "My layout": the wall arranged by hand earlier, or, the first time, what the wall shows now. */
  const customise = () => { if (!cells.some(Boolean)) setCells(wall.slice()); setCustom(true); setPage(0); };
  const fillWith = (group: Tab | '*') => {
    const rank = (c: Cam) => (c.online ? 0 : onDemand(c) ? 1 : 2);
    const order = tabOrder(group);
    setCells(cams.filter(c => inTab(c, group)).sort((a, b) => rank(a) - rank(b) || order(a, b)).slice(0, MAX_CELLS).map(c => c.id));
    setPage(0);
  };
  const clearCell = (i: number) => setCells(cs => cs.map((x, j) => (j === i ? null : x)));

  const onDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault(); setOver(null);
    if (!custom) return;                           // the wall follows the tab: nothing to arrange
    const raw = e.dataTransfer.getData(MIME) || e.dataTransfer.getData('text/plain');
    if (!raw) return;
    const [camId, from] = raw.split('|');
    if (camId) place(camId, i, from !== undefined && from !== '' ? Number(from) : null);
  };

  const chip = (on: boolean) => `px-2.5 py-1 rounded-lg text-[11px] font-semibold border cursor-pointer transition ${on
    ? 'bg-blue-500 text-white border-blue-500'
    : 'bg-white dark:bg-[#181b23] text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-[#262b37] hover:border-blue-400'}`;

  return (
    <div className="p-3 sm:p-4 flex flex-col lg:flex-row gap-3 text-neutral-900 dark:text-neutral-100 lg:h-[calc(100vh-126px)]">
      {/* camera list */}
      <aside className={`lg:w-64 shrink-0 flex-col gap-2 min-h-0 ${listOpen ? 'flex' : 'hidden'}`}>
        <div className="flex items-center gap-2">
          <Video className="w-4 h-4 text-blue-500" />
          <h1 className="text-base font-bold">Video wall</h1>
          <span className="text-[11px] text-neutral-500 ml-auto">{cams.filter(c => c.online).length}/{cams.length} live</span>
        </div>
        <label className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search cameras…"
            className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] text-[12px] outline-none focus:border-blue-400" />
        </label>
        {hold && (
          <div className="rounded-lg border border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10 px-2.5 py-1.5 text-[12px]">
            <div className="font-bold">{hold.name}</div>
            <div className="text-neutral-600 dark:text-neutral-300">{hold.ids.length} cameras: entry, boxes in order, exit</div>
            <Link to="/video-wall" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">Show all cameras</Link>
          </div>
        )}
        <div className={`flex flex-wrap gap-1 ${hold ? 'hidden' : ''}`}>
          <button className={chip(area === 'All')} onClick={() => { setArea('All'); setPage(0); }}>All {cams.length}</button>
          {TABS.filter(a => areaCount[a]).map(a => (
            <button key={a} className={chip(area === a)} onClick={() => { setArea(a); setPage(0); }}>{a} {areaCount[a]}</button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-neutral-600 dark:text-neutral-300 cursor-pointer select-none">
          <input type="checkbox" checked={onlyOnline} onChange={e => setOnlyOnline(e.target.checked)} /> Live cameras only
        </label>
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400">{custom
          ? 'My layout: drag a camera onto a cell, or click it to use the next free cell.'
          : 'The wall shows the cameras of the chosen tab, page after page. Click a camera to go to its page.'}</p>
        {error && outage && <div className="text-[11.5px] text-amber-600 dark:text-amber-400">{cams.length ? 'Camera list is not refreshing — showing the last known list. Retrying…' : 'The camera list could not be loaded. Retrying…'}</div>}
        <ul className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1 max-h-56 lg:max-h-none">
          {list.map(c => {
            const used = onWall.has(c.id);
            return (
              <li key={c.id}>
                <button type="button" draggable={custom}
                  onDragStart={e => { e.dataTransfer.setData(MIME, `${c.id}|`); e.dataTransfer.setData('text/plain', `${c.id}|`); e.dataTransfer.effectAllowed = 'copyMove'; }}
                  onClick={() => addToFirstFree(c.id)}
                  title={used ? 'On the wall: click to go to its page' : custom ? 'Drag onto a cell, or click to add' : 'Not in this tab'}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg border text-[12px] flex items-center gap-2 ${custom ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} transition ${used && custom
                    ? 'bg-orange-50 text-orange-800 border-orange-300 border-l-4 border-l-orange-500 dark:bg-orange-500/15 dark:text-orange-200 dark:border-orange-500/40 dark:border-l-orange-500'
                    : 'bg-white dark:bg-[#181b23] border-neutral-200 dark:border-[#262b37] hover:border-blue-400'}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{c.name || c.id}</span>
                    <span className={`block truncate text-[10px] font-mono ${used && custom ? 'text-orange-700/80 dark:text-orange-300/80' : 'text-neutral-500'}`}>{c.id}{c.ip ? ` · ${c.ip}` : ''}</span>
                  </span>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${c.online ? 'bg-emerald-400' : onDemand(c) ? 'bg-sky-400' : 'bg-rose-500'}`} aria-label={c.online ? 'live' : onDemand(c) ? 'on demand' : 'offline'} title={onDemand(c) ? 'View only: opens when placed on the wall' : undefined} />
                </button>
              </li>
            );
          })}
          {!list.length && !error && <li className="text-[11.5px] text-neutral-500 px-1">No camera matches.</li>}
        </ul>
      </aside>

      {/* wall */}
      {/* The toolbar and the wall share one column that keeps the cameras' 16:9 shape and is as large as the
          space allows. Centred as a block, so with the camera list hidden the buttons sit right above the wall
          instead of spreading over the full page width. */}
      <section ref={wallRef} style={{ containerType: 'size' }}
        className={`flex-1 min-w-0 min-h-0 flex items-start justify-center ${full ? 'bg-black p-2 text-neutral-100' : ''}`}>
        <div className="flex flex-col gap-2" style={{ width: 'min(100cqw, calc((100cqh - 44px) * 16 / 9))' }}>
        <div className="flex flex-wrap items-center gap-1.5 min-h-[36px]">
          <button onClick={() => setListOpen(v => !v)} title={listOpen ? 'Hide the camera list' : 'Show the camera list'}
            className={`${chip(false)} inline-flex items-center gap-1 ${full ? 'hidden' : ''}`}>
            {listOpen ? <PanelLeftClose className="w-3 h-3" /> : <PanelLeftOpen className="w-3 h-3" />} Cameras
          </button>
          {SIZES.map(s => <button key={s} className={chip(n === s)} onClick={() => setN(s)}>{s}×{s}</button>)}
          <button hidden={!!hold} className={chip(custom)} onClick={() => (custom ? (setCustom(false), setPage(0)) : customise())}
            title={custom ? 'On: the wall is arranged by hand. Click to let the wall follow the tabs again (your layout is kept).' : 'Your own wall, arranged by hand (drag cameras onto cells)'}>My layout</button>
          <select value="" hidden={!custom} onChange={e => { if (e.target.value) fillWith(e.target.value as Tab | '*'); }} aria-label="Fill the wall with a group of cameras"
            className="px-2 py-1 rounded-lg border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] text-neutral-700 dark:text-neutral-200 text-[11px] font-semibold cursor-pointer outline-none">
            <option value="">Fill wall with…</option>
            <option value="*">All cameras (live first)</option>
            {TABS.filter(a => areaCount[a]).map(a => <option key={a} value={a}>{a} ({areaCount[a]})</option>)}
          </select>
          <button className={chip(fill)} onClick={() => setFill(v => !v)} title="Fill: the picture covers the whole cell (edges may be cut). Off: the whole picture is shown.">Fill cells</button>
          <span className="text-[11px] text-neutral-500 ml-1 hidden xl:inline">{custom ? `${onPage} of ${shown} in use${pages > 1 ? ` · ${onWall.size} on the wall` : ''}` : `${hold ? hold.name : area === 'All' ? 'All' : area}: ${onWall.size} cameras`}</span>
          {pages > 1 && (
            <span className="inline-flex items-center gap-1" role="navigation" aria-label="Wall pages">
              <button className={chip(false)} disabled={pg === 0} onClick={() => setPage(pg - 1)} aria-label="Previous page" title="Previous page (left arrow key)" style={{ opacity: pg === 0 ? 0.4 : 1 }}>‹</button>
              {Array.from({ length: pages }, (_, i) => (
                <button key={i} className={chip(i === pg)} onClick={() => setPage(i)} aria-label={`Page ${i + 1}`} aria-current={i === pg ? 'page' : undefined}>{i + 1}</button>
              ))}
              <button className={chip(false)} disabled={pg >= pages - 1} onClick={() => setPage(pg + 1)} aria-label="Next page" title="Next page (right arrow key)" style={{ opacity: pg >= pages - 1 ? 0.4 : 1 }}>›</button>
            </span>
          )}
          <button onClick={toggleFull} className={`ml-auto ${chip(full)} inline-flex items-center gap-1`}>
            {full ? <Shrink className="w-3 h-3" /> : <Expand className="w-3 h-3" />} {full ? 'Exit full screen' : 'Full screen'}
          </button>
          <button onClick={() => setCells(cs => cs.map((x, i) => (i >= base && i < base + shown ? null : x)))} disabled={!onPage} hidden={!custom}
            title={pages > 1 ? 'Empties the page on screen; the other pages stay' : undefined}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-neutral-200 dark:border-[#262b37] text-[11px] font-semibold hover:border-rose-400 hover:text-rose-500 disabled:opacity-40 cursor-pointer">
            <Trash2 className="w-3 h-3" /> {pages > 1 ? 'Clear page' : 'Clear wall'}
          </button>
        </div>
        <div className="grid gap-1 w-full" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${n}, minmax(0, 1fr))`, aspectRatio: '16 / 9' }}>
          {Array.from({ length: shown }, (_, k) => {
            const i = base + k;                    // place in the whole wall, over all pages
            const id = wall[i] ?? null; const cam = id ? byId.get(id) : undefined;
            return (
              <div key={i}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== i) setOver(i); }}
                onDragLeave={() => setOver(o => (o === i ? null : o))}
                onDrop={e => onDrop(e, i)}
                className={`relative rounded-md overflow-hidden bg-black border min-h-0 ${over === i ? 'border-blue-400 ring-2 ring-blue-400/40' : 'border-neutral-800'}`}>
                {!id ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-neutral-600 select-none">
                    {custom && <><Plus className="w-5 h-5" /><span className="text-[11px]">Drop here</span></>}
                  </div>
                ) : (
                  <div className="absolute inset-0 group" draggable={custom}
                    onDragStart={e => { e.dataTransfer.setData(MIME, `${id}|${i}`); e.dataTransfer.setData('text/plain', `${id}|${i}`); e.dataTransfer.effectAllowed = 'move'; }}
                    onClick={() => { if (!custom) setBig(id); }} onDoubleClick={() => setBig(id)}
                    title={custom ? undefined : 'Click for live video'}
                    style={{ cursor: custom ? 'grab' : 'pointer' }}>
                    {pictureSrc(cam, n >= 3)
                      ? <img src={pictureSrc(cam, n >= 3)!} alt={id} draggable={false}
                          className={`w-full h-full pointer-events-none ${cam && !cam.online && !onDemand(cam) ? 'opacity-60' : ''} ${fill ? 'object-cover' : 'object-contain'}`} />
                      : <div className="absolute inset-0 flex items-center justify-center text-[11px] text-neutral-500 select-none">{cam ? 'No picture yet' : 'Loading…'}</div>}
                    {/* one holding area's wall: live from the camera's plain frame, over the saved picture */}
                    {hold && cam?.online && cam.analytics !== false && (
                      <StillFeed camId={cam.id} width={n >= 5 ? 480 : 800} every={1000}
                        className={`absolute inset-0 w-full h-full pointer-events-none ${fill ? 'object-cover' : 'object-contain'}`} />
                    )}
                    {hold && cam?.online && cam.analytics !== false ? (
                      <span className="absolute bottom-1 left-1 px-1 rounded bg-emerald-600/90 text-[10px] font-bold text-white pointer-events-none"
                        title="A new picture every second">LIVE</span>
                    ) : cam?.snapshot_ts && (
                      <span className="absolute bottom-1 left-1 px-1 rounded bg-black/60 text-[10px] text-neutral-200 tabular-nums pointer-events-none"
                        title="When this picture was taken (a new one every 10 minutes)">{pictureTime(cam.snapshot_ts)}</span>
                    )}
                    <div className="absolute top-0 inset-x-0 px-2 py-1 flex items-center gap-1.5 bg-gradient-to-b from-black/80 to-transparent text-white">
                      <span className={`w-1.5 h-1.5 rounded-full ${!cam ? 'bg-neutral-400 animate-pulse' : cam.online ? 'bg-emerald-400' : onDemand(cam) ? 'bg-sky-400' : 'bg-rose-500'}`} />
                      <span className="text-[11px] font-semibold truncate" title={cam?.name || id}>{hold && cam?.name ? cam.name.replace(/^Holding Area \d+\s*(·\s*)?/i, '') || cam.name : cam?.name || id}</span>
                      {cam && !cam.online && !onDemand(cam) && <span className="text-[9.5px] px-1 rounded bg-rose-600">OFFLINE</span>}
                      {cam?.analytics === false && <span className="text-[9.5px] px-1 rounded bg-black/60">VIEW ONLY</span>}
                      {cam?.online && cam.analytics !== false && cam.tracks != null && (
                        <span className="ml-auto inline-flex items-center gap-0.5 px-1.5 rounded bg-black/60 text-[10.5px] font-bold" title="People the camera counts right now">
                          <Users className="w-2.5 h-2.5" /> {cam.tracks}
                        </span>
                      )}
                      <span className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                        <button onClick={() => setBig(id)} title="Enlarge" className="p-1 rounded bg-black/60 hover:bg-black cursor-pointer"><Maximize2 className="w-3 h-3" /></button>
                        {custom && <button onClick={() => clearCell(i)} title="Remove from the wall" className="p-1 rounded bg-black/60 hover:bg-rose-600 cursor-pointer"><X className="w-3 h-3" /></button>}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {big && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setBig(null)}>
          <div className="w-[1100px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-3 py-2 border-b border-[#262b37] text-neutral-100">
              <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-rose-600 font-bold">LIVE</span>
              <span className="text-[13px] font-bold truncate">{byId.get(big)?.name || big}</span>
              <span className="font-mono text-[11px] text-neutral-400">{big}</span>
              <button onClick={() => setBig(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(big)}/snapshot.jpg`} alt={big} className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
